import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import FeatureSettingsSection from './FeatureSettingsSection';

/**
 * SettingsHub — one settings surface, three tiers, gated by role.
 *
 * The two User Management screens previously each rendered their own
 * settings blob, which is how the same controls ended up duplicated and
 * inconsistent. This centralizes them and, more importantly, separates
 * settings by the scope they ACTUALLY have in the database:
 *
 *   Personal      (any user)      → org_feature_settings scope='user'
 *                                   Overrides just for me.
 *   Organization  (company admin) → org_feature_settings scope='org'
 *                                   + org_settings (LinkedIn cooldown)
 *                                   Defaults for everyone in my firm.
 *   System        (system admin)  → suggestions_config
 *                                   Global. Affects EVERY organization.
 *
 * That last tier matters: AI spend caps are stored in a single global
 * key-value table with no firm column, so they are genuinely platform-wide.
 * Showing them to a company admin as "your organization's limit" would be
 * wrong — one firm's admin would be changing every firm's budget.
 *
 * Tabs only render for tiers the current user can actually use, so a
 * regular user sees a single Personal pane with no empty scaffolding.
 */

const auth = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Guard against the SPA index.html fallback being parsed as JSON when the
// API routes aren't deployed yet. See FeatureSettingsSection for the full note.
async function readJson(res) {
  const type = res.headers.get('content-type') || '';
  if (!type.includes('application/json')) return null;
  try { return await res.json(); } catch { return null; }
}

const Unavailable = ({ what, files }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-2xl">
    <p className="text-sm text-amber-900 font-medium">{what} aren't available on this server yet.</p>
    <p className="text-xs text-amber-800 mt-1">Needs deploying: {files}</p>
  </div>
);

// ─── Personal tier ─────────────────────────────────────────────────────
// A user's overrides only override the keys they actually set. Anything
// left on "Follow organization default" stays live-linked to the org, so
// changing the org default later still reaches them.
const PersonalSettings = () => {
  const [resolved, setResolved] = useState(null);
  const [mine, setMine] = useState({});
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [unavailable, setUnavailable] = useState(false);

  const load = async () => {
    try {
      const [rRes, mRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/feature-settings`, { headers: auth() }),
        fetch(`${API_BASE_URL}/api/feature-settings/me`, { headers: auth() }),
      ]);
      const r = await readJson(rRes);
      const m = await readJson(mRes);
      if (!r || rRes.status === 404) { setUnavailable(true); return; }
      setResolved(r);
      if (m) setMine(m);
    } catch (e) {
      console.error('Failed to load personal settings:', e);
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (body) => {
    setMsg('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/feature-settings/me`, {
        method: 'PUT', headers: auth(), body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Save failed');
      setResolved(await res.json());
      const mRes = await fetch(`${API_BASE_URL}/api/feature-settings/me`, { headers: auth() });
      if (mRes.ok) setMine(await mRes.json());
      setMsg('Saved');
      setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg('Error: ' + e.message);
    }
  };

  if (loading) {
    return <div className="bg-white rounded-lg border p-6 text-sm text-gray-400 animate-pulse">Loading your settings…</div>;
  }
  if (unavailable) {
    return <Unavailable what="Personal settings" files="server/src/routes/featureSettings.js, server/src/services/orgFeatureSettings.js, and the updated server.js" />;
  }

  const TRIGGER_LABELS = {
    on_transcription: 'After a call is transcribed',
    on_note: 'After a note is saved',
    on_mention: 'When a candidate is @-mentioned',
  };

  const overridden = mine.suggestions_scan_triggers !== undefined;
  const effective = resolved?.suggestions_scan_triggers || [];
  const orgSummary = effective.length
    ? effective.map(t => TRIGGER_LABELS[t] || t).join(', ')
    : 'Manual only';

  // A user's set is all-or-nothing against the org's: either follow it, or
  // define your own. Merging the two per-key would make "why did this scan?"
  // very hard to answer.
  const mineSet = mine.suggestions_scan_triggers || [];
  const toggleMine = (key) => {
    const next = mineSet.includes(key) ? mineSet.filter(t => t !== key) : [...mineSet, key];
    patch({ suggestions_scan_triggers: next });
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-lg font-semibold text-gray-900">Your preferences</h2>
        <p className="text-sm text-gray-500 mt-0.5 mb-4">
          These apply to you only. Anything left on the organization default will follow future changes your admin makes.
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-1.5">Automatic suggestion scans</label>

        <label className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer mb-2">
          <input
            type="radio" name="personal-scan" checked={!overridden}
            onChange={() => patch({ suggestions_scan_triggers: null })}
            className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
          />
          <span>
            <span className="block text-sm font-medium text-gray-800">Follow organization default</span>
            <span className="block text-xs text-gray-500">Currently: {orgSummary}</span>
          </span>
        </label>

        <label className="flex items-start gap-3 px-3 py-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
          <input
            type="radio" name="personal-scan" checked={overridden}
            onChange={() => patch({ suggestions_scan_triggers: effective })}
            className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
          />
          <span>
            <span className="block text-sm font-medium text-gray-800">Choose my own</span>
            <span className="block text-xs text-gray-500">Set which events scan my candidates.</span>
          </span>
        </label>

        {overridden && (
          <div className="mt-3 pl-3 border-l-2 border-blue-200 space-y-2">
            {Object.entries(TRIGGER_LABELS).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox" checked={mineSet.includes(key)}
                  onChange={() => toggleMine(key)}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{label}</span>
              </label>
            ))}
            {mineSet.length === 0 && (
              <p className="text-xs text-gray-500">Nothing selected — manual only for you.</p>
            )}
          </div>
        )}

        {msg && (
          <p className={`text-sm mt-3 ${msg.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>{msg}</p>
        )}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-xs text-gray-600">
          The profile review flag (which widgets mark a candidate as needing review) is set by your
          organization and isn't overridable per person — it keeps the review state meaningful for everyone.
        </p>
      </div>
    </div>
  );
};

// ─── Organization spend limits ─────────────────────────────────────────
// A company admin sets their own org's budget here. The platform ceiling
// still applies, so anything above it is clamped — shown explicitly rather
// than silently, since a saved-but-ignored number is worse than a rejected
// one.
const OrgSpendLimits = () => {
  const [own, setOwn] = useState(null);
  const [ceiling, setCeiling] = useState(null);
  const [effective, setEffective] = useState(null);
  const [orgName, setOrgName] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/candidates/suggestions/caps`, { headers: auth() });
        const body = await readJson(res);
        if (res.ok && body && body.scope === 'org') {
          setOwn(body.own);
          setCeiling(body.ceiling);
          setEffective(body.effective);
          setOrgName(body.organization);
        }
      } catch (e) {
        console.error('Failed to load org limits:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const field = (key) => ({
    value: own && own[key] ? (own[key] / 100).toFixed(2) : '',
    onChange: (e) => {
      const v = e.target.value === '' ? 0 : Math.max(0, parseFloat(e.target.value) || 0);
      setOwn(prev => ({ ...prev, [key]: Math.round(v * 100) }));
    },
  });

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/candidates/suggestions/caps`, {
        method: 'PUT', headers: auth(),
        body: JSON.stringify({
          daily_cap_cents_user: own.dailyCapCentsUser,
          daily_cap_cents_org: own.dailyCapCentsOrg,
          monthly_cap_cents_org: own.monthlyCapCentsOrg,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Save failed');
      const body = await res.json();
      setOwn(body.own); setCeiling(body.ceiling); setEffective(body.effective);
      setMsg('Saved'); setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="bg-white rounded-lg border p-6 text-sm text-gray-400 animate-pulse">Loading spend limits…</div>;
  }
  if (!own) return null;

  const FIELDS = [
    { key: 'dailyCapCentsUser', label: 'Daily, per user', hint: 'Stops one person running away with it.' },
    { key: 'dailyCapCentsOrg', label: 'Daily, whole organization', hint: 'Your ceiling for any single day.' },
    { key: 'monthlyCapCentsOrg', label: 'Monthly, whole organization', hint: 'The number that shows up on the bill.' },
  ];

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">AI spend limits</h2>
          <p className="text-sm text-gray-500">
            Caps on AI suggestion scanning for{' '}
            <span className="font-medium text-gray-700">{orgName || 'your organization'}</span>.
            Usage is billed and reported under this name. Blank or 0 leaves a limit off.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {FIELDS.map(f => {
          const ceil = ceiling ? ceiling[f.key] : 0;
          const eff = effective ? effective[f.key] : 0;
          const clamped = ceil > 0 && own[f.key] > ceil;
          return (
            <div key={f.key}>
              <label className="block text-xs font-medium text-gray-700 mb-1">{f.label}</label>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                <input type="number" min="0" step="0.50" placeholder="No limit" {...field(f.key)}
                  className={`w-full pl-5 pr-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                    clamped ? 'border-amber-300 focus:ring-amber-500' : 'focus:ring-blue-500'
                  }`} />
              </div>
              <p className="text-[10px] text-gray-400 mt-1 leading-snug">{f.hint}</p>
              {ceil > 0 && (
                <p className={`text-[10px] mt-0.5 ${clamped ? 'text-amber-600' : 'text-gray-400'}`}>
                  {clamped
                    ? `Above the platform ceiling — ${money(ceil)} applies.`
                    : `Platform ceiling: ${money(ceil)}`}
                </p>
              )}
              {eff > 0 && <p className="text-[10px] text-blue-600 mt-0.5">In effect: {money(eff)}</p>}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button onClick={save} disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save spend limits'}
        </button>
        {msg && <span className={`text-sm ${msg.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>{msg}</span>}
      </div>
    </div>
  );
};

// ─── System tier ───────────────────────────────────────────────────────
// Spend caps live in a single global table. Editing them here changes the
// budget for EVERY organization on the platform, which is why this pane is
// system-admin only and says so plainly.
const SystemSettings = () => {
  const [caps, setCaps] = useState(null);
  const [orgRows, setOrgRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/candidates/suggestions/caps`, { headers: auth() });
        const body = await readJson(res);
        if (res.ok && body) {
          setCaps(body.system || body);
          setOrgRows(body.organizations || []);
        }
      } catch (e) {
        console.error('Failed to load caps:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const field = (key) => ({
    value: caps && caps[key] ? (caps[key] / 100).toFixed(2) : '',
    onChange: (e) => {
      const v = e.target.value === '' ? 0 : Math.max(0, parseFloat(e.target.value) || 0);
      setCaps(prev => ({ ...prev, [key]: Math.round(v * 100) }));
    },
  });

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/candidates/suggestions/caps`, {
        method: 'PUT', headers: auth(),
        body: JSON.stringify({
          firm_id: null,
          daily_cap_cents_user: caps.dailyCapCentsUser,
          daily_cap_cents_org: caps.dailyCapCentsOrg,
          monthly_cap_cents_org: caps.monthlyCapCentsOrg,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Save failed');
      const body = await res.json();
      setCaps(body.system || body);
      setMsg('Saved'); setTimeout(() => setMsg(''), 2500);
    } catch (e) {
      setMsg('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="bg-white rounded-lg border p-6 text-sm text-gray-400 animate-pulse">Loading system settings…</div>;
  }
  if (!caps) {
    return <Unavailable what="System settings" files="server/src/routes/suggestions.js and server/src/services/suggestionsService.js" />;
  }

  const allOff = !caps.dailyCapCentsUser && !caps.dailyCapCentsOrg && !caps.monthlyCapCentsOrg;

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
        <p className="text-xs text-purple-900">
          <strong>Platform ceiling.</strong> These are the maximum limits any organization can set for
          itself. A company admin can tighten their own budget below these, never above.
        </p>
      </div>

      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-lg font-semibold text-gray-900">AI spend limits</h2>
          {allOff && (
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">No limits set</span>
          )}
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Caps on AI suggestion scanning. Blank or 0 leaves a limit off. A scan is blocked once the relevant
          limit is already reached, so a single run can cross it by a few cents at most.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { key: 'dailyCapCentsUser', label: 'Daily, per user', hint: 'Stops one person running away with it.' },
            { key: 'dailyCapCentsOrg', label: 'Daily, per organization', hint: 'Ceiling for any single day.' },
            { key: 'monthlyCapCentsOrg', label: 'Monthly, per organization', hint: 'The number that shows up on the bill.' },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-gray-700 mb-1">{f.label}</label>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                <input type="number" min="0" step="0.50" placeholder="No limit" {...field(f.key)}
                  className="w-full pl-5 pr-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <p className="text-[10px] text-gray-400 mt-1 leading-snug">{f.hint}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 mt-4">
          <button onClick={save} disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save limits'}
          </button>
          {msg && <span className={`text-sm ${msg.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>{msg}</span>}
        </div>

        <p className="text-xs text-gray-400 mt-3 pt-3 border-t">
          Observed per-scan costs are on the AI Usage tab — set these from that data rather than guessing.
        </p>
      </div>

      {/* What each organization has set for itself, for oversight. */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="px-4 py-2.5 border-b bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-900">Organization limits</h3>
        </div>
        {orgRows.length === 0 ? (
          <p className="text-sm text-gray-500 px-4 py-6">
            No organization has set its own limits — all follow the platform ceiling above.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="px-4 py-2 font-medium">Organization</th>
                <th className="px-4 py-2 font-medium text-right">Daily / user</th>
                <th className="px-4 py-2 font-medium text-right">Daily / org</th>
                <th className="px-4 py-2 font-medium text-right">Monthly</th>
              </tr>
            </thead>
            <tbody>
              {orgRows.map(o => (
                <tr key={o.firm_id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium text-gray-900">{o.organization}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700">{o.daily_cap_cents_user ? money(o.daily_cap_cents_user) : '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700">{o.daily_cap_cents_org ? money(o.daily_cap_cents_org) : '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-gray-700">{o.monthly_cap_cents_org ? money(o.monthly_cap_cents_org) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ─── Hub ───────────────────────────────────────────────────────────────
const SettingsHub = ({ currentUser, orgPanel }) => {
  const isSystemAdmin = currentUser?.user_type === 'admin';
  const isCompanyAdmin = isSystemAdmin
    || currentUser?.company_role === 'company_admin'
    || currentUser?.company_role === 'admin';

  const tiers = [
    { key: 'personal', label: 'Your preferences', show: true },
    { key: 'org', label: 'Organization', show: isCompanyAdmin },
    { key: 'system', label: 'System', show: isSystemAdmin },
  ].filter(t => t.show);

  // Admins land on Organization (the tier they most often came for);
  // everyone else has only Personal anyway.
  //
  // currentUser arrives asynchronously in one of the two host screens, so the
  // initial value can be computed before the role is known. This effect
  // corrects the landing tab once the role resolves — but only while the user
  // hasn't chosen a tab themselves, so it never yanks the pane out from under
  // someone mid-click.
  const [tier, setTier] = useState(isCompanyAdmin ? 'org' : 'personal');
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched && isCompanyAdmin && tier === 'personal') setTier('org');
  }, [isCompanyAdmin, touched, tier]);

  const pickTier = (key) => { setTouched(true); setTier(key); };

  // Guard against a tier disappearing if the role resolves downward.
  useEffect(() => {
    if (tier === 'system' && !isSystemAdmin) setTier(isCompanyAdmin ? 'org' : 'personal');
    else if (tier === 'org' && !isCompanyAdmin) setTier('personal');
  }, [tier, isSystemAdmin, isCompanyAdmin]);

  return (
    <div className="space-y-4">
      {tiers.length > 1 && (
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
          {tiers.map(t => (
            <button key={t.key} onClick={() => pickTier(t.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                tier === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}>
              {t.label}
              {t.key === 'system' && (
                <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded bg-purple-100 text-purple-700 align-middle">GLOBAL</span>
              )}
            </button>
          ))}
        </div>
      )}

      {tier === 'personal' && <PersonalSettings />}

      {tier === 'org' && isCompanyAdmin && (
        <div className="space-y-6">
          {orgPanel}
          <FeatureSettingsSection />
          <OrgSpendLimits />
        </div>
      )}

      {tier === 'system' && isSystemAdmin && <SystemSettings />}
    </div>
  );
};

export default SettingsHub;
